import React from 'react';
import { sb } from '../../../modules/state';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ProtocolPage } from './ProtocolPage';
import { Modal } from '../../ui/Modal';

export function ProtocolOMAW() {
  const { currentUser, isSubscribed } = useAuth();
  const navigate = useNavigate();
  const [isAbandonModalOpen, setIsAbandonModalOpen] = React.useState(false);
  const [activeFast, setActiveFast] = React.useState(null);
  const [stopReason, setStopReason] = React.useState('Hunger');

  // Check for active fast on mount
  React.useEffect(() => {
    const checkActiveFast = async () => {
      if (!sb || !currentUser?.id) return;

      try {
        const { data } = await sb
          .from('fasting_sessions')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('status', 'active')
          .single();
        setActiveFast(data);
      } catch (error) {
        // No active fast found, which is fine
      }
    };

    checkActiveFast();
  }, [currentUser]);

  const handleStartProtocol = async () => {
    if (!sb || !currentUser?.id) {
      console.error('[ProtocolOMAW] No user or Supabase');
      return;
    }

    // Check if there's an active fast
    if (activeFast) {
      setIsAbandonModalOpen(true);
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      await sb.from('profiles').upsert({
        id: currentUser.id,
        current_protocol: 'OMAW',
        protocol_start_date: today,
      });
      console.log('[ProtocolOMAW] Protocol started: OMAW');
      navigate('/dashboard');
    } catch (error) {
      console.error('[ProtocolOMAW] Error starting protocol:', error);
    }
  };

  const handleEnterBloodResults = () => {
    navigate('/blood');
  };

  const handleConfirmAbandon = async () => {
    if (!sb || !currentUser?.id || !activeFast) {
      setIsAbandonModalOpen(false);
      return;
    }

    try {
      // Mark current fast as abandoned
      await sb.from('fasting_sessions').update({
        fast_end: new Date().toISOString(),
        status: 'abandoned',
        reason: stopReason,
      }).eq('id', activeFast.id);

      // Switch protocol
      const today = new Date().toISOString().split('T')[0];
      await sb.from('profiles').upsert({
        id: currentUser.id,
        current_protocol: 'OMAW',
        protocol_start_date: today,
      });

      setActiveFast(null);
      setIsAbandonModalOpen(false);
      console.log('[ProtocolOMAW] Fast abandoned, protocol set to OMAW');
      navigate('/dashboard');
    } catch (error) {
      console.error('[ProtocolOMAW] Error abandoning fast:', error);
    }
  };

  const biochemistryList = (
    <ul className="list-disc list-inside space-y-1">
      <li>Maximum mitophagy — damaged mitochondria recycled at peak rate</li>
      <li>Brain shifts almost entirely to ketone metabolism</li>
      <li>Beta-hydroxybutyrate increases BDNF — neuroprotection</li>
      <li>Systemic anti-inflammatory effect — IL-6 and CRP drop dramatically</li>
      <li>Deep autophagy — cellular renewal at maximum</li>
    </ul>
  );

  const handleSubscribe = () => {
    const email = currentUser?.email || '';
    const freeTrialUrl = 'https://222fasting.lemonsqueezy.com/checkout/buy/cc5bb239-4095-4549-87c5-70a900626c44?embed=1&checkout[email]=' + encodeURIComponent(email);
    window.LemonSqueezy?.Url?.Open(freeTrialUrl);
  };

  return (
    <>
      <ProtocolPage
        protocolName="OMAW"
        tagline="Deep Metabolic Reset"
        tierBadge="Paid"
        whatIs="One Meal A Week. 5–7 day fasting windows. Maximum metabolic pressure. The body reaches peak mitophagy and profound ketosis."
        biochemistry={biochemistryList}
        whoItIsFor="Users whose insulin confirms above 10.4 mIU/L and who require deeper metabolic intervention than TMAW provides."
        francoisSays="OMAW is where the real reset happens. The brain clarity at day 5 is unlike anything I can describe. The body becomes extraordinarily efficient."
        ctaText="Select Protocol"
        isLocked={!isSubscribed}
        lockedMessage="OMAW requires a 222fasting subscription"
        lockedCtaText="Start Free Trial"
        onCtaClick={handleSubscribe}
      />

      {/* Abandon Modal */}
      <Modal
        isOpen={isAbandonModalOpen}
        onClose={() => setIsAbandonModalOpen(false)}
        title="Are you ending your fast early?"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-2">
              Reason:
            </label>
            <select
              value={stopReason}
              onChange={(e) => setStopReason(e.target.value)}
              className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-on-surface focus:border-primary focus:outline-none"
            >
              <option>Hunger</option>
              <option>Medical</option>
              <option>Social</option>
              <option>Personal</option>
              <option>Other</option>
            </select>
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setIsAbandonModalOpen(false)}
              className="flex-1 rounded-lg bg-surface-container px-4 py-2 text-base font-medium text-on-surface hover:bg-surface-container-hover"
            >
              Continue Fasting
            </button>
            <button
              onClick={handleConfirmAbandon}
              className="flex-1 rounded-lg bg-error-container px-4 py-2 text-base font-medium text-error hover:bg-error-container/80"
            >
              Confirm Stop
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
